import { usePageMeta } from "../hooks/usePageMeta";
import { FadeIn } from "../components/site/FadeIn";
import { SectionHeading } from "../components/site/SectionHeading";
import { StatTile } from "../components/site/StatTile";
import { MediaCard } from "../components/site/MediaCard";
import { ExternalLink } from "lucide-react";
import {
  LIVE_STUDIO, COURSES, CURRICULUM_DESIGN, COURSERA_COURSES,
  EVALUATION_TILES, EVALUATIONS, OUTCOME_TILES, OUTCOMES_MOBILE_CAPTION, STUDENT_VIDEOS,
} from "../data/teaching";

export default function Teaching() {
  usePageMeta(
    "Teaching — Keyur Sorathia",
    "Studio teaching built on live briefs from funded projects, with student evaluations and outcomes."
  );
  return (
    <div data-testid="teaching-page">
      <section className="section">
        <div className="container-site">
          <FadeIn>
            <h1 className="text-h1 mb-6" style={{ color: "var(--navy-700)", maxWidth: 980 }}>
              I teach design the way I practise it: a real brief, real users, and work that actually gets built.
            </h1>
            <p className="body-lg measure">
              My studios run on live briefs from my funded projects. Students meet the users, work with developers who build what they design, and test the result. Nine peer-reviewed papers have come from coursework alone, seven more from the theses that follow.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="section" style={{ background: "var(--surface-alt)" }}>
        <div className="container-site">
          <FadeIn>
            <SectionHeading eyebrow="How the studio runs" title="The live studio" />
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {LIVE_STUDIO.map((c) => (
              <FadeIn key={c.title}>
                <div className="h-full bg-white p-6" style={{ border: "1px solid var(--border-default)", borderRadius: 10 }}>
                  <h3 className="text-h4 mb-3">{c.title}</h3>
                  <p className="body-sm m-0">{c.body}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-site">
          <FadeIn>
            <SectionHeading eyebrow="Courses" title="What I teach" />
          </FadeIn>

          <div className="hidden md:block" style={{ maxWidth: 820 }}>
            <table className="spec-table" data-testid="courses-table">
              <thead>
                <tr>
                  <th scope="col">Course</th>
                  <th scope="col">Level</th>
                </tr>
              </thead>
              <tbody>
                {COURSES.map((c) => (
                  <tr key={c.course}>
                    <td style={{ color: "var(--ink-900)", fontWeight: 500 }}>{c.course}</td>
                    <td>{c.level}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden flex flex-col" data-testid="courses-list-mobile">
            {COURSES.map((c) => (
              <div key={c.course} className="py-4" style={{ borderBottom: "1px solid var(--border-default)" }}>
                <p className="body-sm font-medium m-0 mb-1" style={{ color: "var(--ink-900)" }}>{c.course}</p>
                <p className="caption m-0">
                  {c.level}
                  {c.mobileDetail ? ` · ${c.mobileDetail}` : ""}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section" style={{ background: "var(--surface-alt)" }}>
        <div className="container-site">
          <FadeIn>
            <SectionHeading eyebrow="Also teaching publicly" title="Beyond the classroom: Coursera and Intellipaat" />
            <p className="body-lg measure mb-10">
              Alongside classroom teaching at IIT Guwahati, three courses built from this teaching run publicly on Coursera and are open to anyone. I also deliver a postgraduate certification in UX and UI design with Intellipaat and the E&ICT Academy, which has run for roughly twenty cohorts of about ninety learners each.
            </p>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {COURSERA_COURSES.map((c) => (
              <FadeIn key={c.title}>
                <a
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`coursera-card-${c.url.split("/").pop()}`}
                  className="card-hover flex flex-col h-full bg-white p-6"
                  style={{ border: "1px solid var(--border-default)", borderRadius: 10, textDecoration: "none", borderTop: "4px solid var(--accent-teal)" }}
                >
                  <p className="eyebrow m-0 mb-3" style={{ color: "var(--navy-500)" }}>Coursera</p>
                  <h3 className="text-h4 mb-3 flex-1" style={{ color: "var(--navy-700)" }}>{c.title}</h3>
                  <span className="body-sm font-medium flex items-center gap-2" style={{ color: "var(--navy-500)" }}>
                    Open the course <ExternalLink size={14} aria-hidden="true" />
                  </span>
                </a>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-site">
          <FadeIn>
            <SectionHeading eyebrow="Programme design" title="Curriculum design" />
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {CURRICULUM_DESIGN.map((c) => (
              <FadeIn key={c.title}>
                <div className="h-full p-6" style={{ background: "var(--navy-100)", borderRadius: 10 }}>
                  <h3 className="text-h4 mb-2">{c.title}</h3>
                  <p className="body-sm m-0">{c.detail}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="section" style={{ background: "var(--surface-alt)" }}>
        <div className="container-site">
          <FadeIn>
            <SectionHeading eyebrow="Evidence" title="Evaluations" />
            <p className="body-default measure mb-10">
              Responses to "Overall, the teaching by the instructor was excellent" on the official IIT Guwahati Academic Affairs instrument, scale one to five. Class sizes ran from 16 to 59, so these are not small-cohort results.
            </p>
          </FadeIn>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {EVALUATION_TILES.map((t) => (
              <StatTile key={t.label} value={t.value} label={t.label} />
            ))}
          </div>

          <div className="hidden md:block">
            <table className="spec-table" data-testid="evaluations-table">
              <thead>
                <tr>
                  <th scope="col">Course</th>
                  <th scope="col">Average, out of five</th>
                </tr>
              </thead>
              <tbody>
                {EVALUATIONS.map((e) => (
                  <tr key={`${e.course}-${e.semester}`}>
                    <td style={{ color: "var(--ink-900)", fontWeight: 500 }}>{e.course}</td>
                    <td>{e.average}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden flex flex-col gap-4" data-testid="evaluations-list-mobile">
            {EVALUATIONS.map((e) => (
              <div key={`${e.course}-${e.semester}`} className="bg-white p-4" style={{ border: "1px solid var(--border-default)", borderRadius: 8 }}>
                <p className="body-sm font-medium m-0 mb-2" style={{ color: "var(--ink-900)" }}>{e.course}</p>
                <p className="caption m-0"><span className="font-semibold">Average, out of five:</span> {e.average}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section" style={{ background: "var(--navy-100)" }}>
        <div className="container-site">
          <FadeIn>
            <SectionHeading eyebrow="Outcomes" title="What the teaching produces" />
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {OUTCOME_TILES.map((t) => (
              <FadeIn key={t.label}>
                <div className="p-6 h-full" style={{ background: "#fff", borderRadius: "var(--radius-md)" }}>
                  <div className="stat-value mb-2">{t.value}</div>
                  <div className="stat-label">{t.label}</div>
                </div>
              </FadeIn>
            ))}
          </div>
          <p className="caption mt-6 measure md:hidden">{OUTCOMES_MOBILE_CAPTION}</p>
        </div>
      </section>

      <section className="section">
        <div className="container-site">
          <FadeIn>
            <SectionHeading eyebrow="Student work" title="Watch the work, not just read about it" />
            <p className="body-lg measure mb-10">
              A selection of project videos made by students in these courses. More are added as they come in.
            </p>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-testid="student-videos-grid">
            {STUDENT_VIDEOS.map((v) => (
              <FadeIn key={v.id}>
                <MediaCard title={v.title} id={v.id} url={v.url} caption="YouTube" />
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
